/**
 * StatusTimeline (PR-53, DES-14).
 *
 * Five-step horizontal timeline rendered above the deliverable block on the
 * subscriber-facing task detail. Steps:
 *   Submitted -> Eric reviewing -> In progress -> Awaiting your review -> Approved
 *
 * Current step = Cotton Rose; completed = Oxford Navy; future = muted Linen.
 * Renders alongside (never replacing) the StatusPill.
 *
 * OQ-13 (open question): the PRD asks Eric to confirm these exact step labels
 * and any tone tweaks before launch. V1 interpretation: use the labels
 * verbatim from DES-14 / PR-53. Documented in IMPL-NOTES; not a blocker.
 *
 * Status -> step mapping (OQ-13-adjacent interpretation): the 11 task_status
 * values are collapsed onto the 5 subscriber-facing steps. intake/backlog/
 * ready_to_pull => "Submitted"; pulled => "Eric reviewing"; in_progress/
 * agent_draft/eric_reviewing/ready_to_deliver => "In progress"; delivered =>
 * "Awaiting your review"; approved => "Approved"; split is terminal and shows
 * "Submitted" (the parent was reshaped into children).
 */

import type { TaskStatus } from '@studio/api-schemas';

const STEPS = [
  'Submitted',
  'Eric reviewing',
  'In progress',
  'Awaiting your review',
  'Approved',
] as const;

function stepIndexForStatus(status: TaskStatus): number {
  switch (status) {
    case 'intake_shape_check':
    case 'backlog':
    case 'ready_to_pull':
    case 'split':
      return 0;
    case 'pulled':
      return 1;
    case 'in_progress':
    case 'agent_draft':
    case 'eric_reviewing':
    case 'ready_to_deliver':
      return 2;
    case 'delivered':
      return 3;
    case 'approved':
      return 4;
    default:
      return 0;
  }
}

export function StatusTimeline({ status }: { status: TaskStatus }) {
  const current = stepIndexForStatus(status);
  return (
    <ol
      className="flex w-full items-start"
      aria-label="Task progress"
    >
      {STEPS.map((label, i) => {
        const state =
          i < current ? 'done' : i === current ? 'current' : 'future';
        const isLast = i === STEPS.length - 1;
        return (
          <li
            key={label}
            className={[
              'flex items-center',
              isLast ? 'flex-none' : 'flex-1',
            ].join(' ')}
          >
            <div className="flex flex-col items-center gap-1.5">
              {/* Step node: filled navy + check when done, navy ring + inner
                  dot when current, line-dark ring when future. Matches the
                  prototype StatusTimeline circle treatment. */}
              <span
                className={[
                  'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border-2',
                  state === 'done'
                    ? 'border-[color:var(--color-navy)] bg-[color:var(--color-navy)] text-[color:var(--color-white)]'
                    : state === 'current'
                      ? 'border-[color:var(--color-navy)] bg-[color:var(--color-white)]'
                      : 'border-[color:var(--color-line-dark)] bg-transparent',
                ].join(' ')}
                aria-hidden="true"
              >
                {state === 'done' ? (
                  <span className="text-[11px] leading-none">&#10003;</span>
                ) : state === 'current' ? (
                  <span className="h-2 w-2 rounded-full bg-[color:var(--color-navy)]" />
                ) : null}
              </span>
              <span
                className={[
                  'whitespace-nowrap text-center text-[10px] leading-tight',
                  state === 'current'
                    ? 'font-medium text-[color:var(--color-black)]'
                    : state === 'done'
                      ? 'text-[color:var(--color-text-mute)]'
                      : 'text-[color:var(--color-gray-300)]',
                ].join(' ')}
              >
                {label}
              </span>
            </div>
            {!isLast ? (
              <span
                className={[
                  'mx-1.5 mb-5 h-0.5 min-w-4 flex-1',
                  state === 'done'
                    ? 'bg-[color:var(--color-navy)]'
                    : 'bg-[color:var(--color-gray-100)]',
                ].join(' ')}
                aria-hidden="true"
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
